import type { CredentialResponse } from "@react-oauth/google";
import type { z } from "zod";
import type { AgentBetaAccountMode } from "./components/AccountStep";

import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { agentBetaRouter } from "@v2/trpc/routes/agentBeta";
import { authRouter } from "@v2/trpc/routes/auth";
import { userUtils } from "@v2/trpc/routes/user";
import { workspaceUtils } from "@v2/trpc/routes/workspace";
import { routes } from "@v2/utils/router";
import { storeToken } from "@v2/utils/store-token";
import { useForm } from "react-hook-form";
import {
  AGENT_BETA_OTHER_OPTION_VALUE,
  agentBetaAnnualRevenueBuckets,
  agentBetaMultiSelectQuestions,
  agentBetaProfileTypeSchema,
  agentBetaQuestionAnswersFormSchema,
  collectAgentBetaUrlParams,
  encodeAgentBetaOtherAnswer,
} from "schemas/agentBeta";
import { ReploError } from "schemas/errors";
import { z as zod } from "zod";

class AgentBetaWaitlistError extends ReploError {}

const agentBetaFormSchema = zod.object({
  firstName: zod.string().trim().min(1, "Enter your first name"),
  lastName: zod.string().trim().min(1, "Enter your last name"),
  email: zod.email("Enter a valid email address"),
  profileType: agentBetaProfileTypeSchema,
  // NOTE (Yuxin, 2026-07-30): Starts null so nothing is pre-selected; the
  // refine forces a conscious choice instead of letting people submit the
  // lowest bucket by default.
  revenueBucketIndex: zod
    .number()
    .int()
    .min(0)
    .max(agentBetaAnnualRevenueBuckets.length - 1)
    .nullable()
    .refine((value): value is number => value !== null, {
      error: "Select an annual revenue range",
    }),
  // NOTE (Max, 2026-07-30): One string[] entry per multi-select question,
  // keyed by slug; serialized to a `slug -> csv` map on submit. Every question
  // is required — the shared schema attaches the error to each question's field.
  questionAnswers: agentBetaQuestionAnswersFormSchema,
  // NOTE (Yuxin, 2026-07-30): Free text for each question's "Other" option,
  // keyed by slug; folded into the csv as `other:<text>` on submit.
  questionOtherText: zod.record(zod.string(), zod.string()),
  password: zod.string(),
});

const emptyQuestionAnswers = (): Record<string, string[]> => {
  return Object.fromEntries(
    agentBetaMultiSelectQuestions.map((question) => [question.slug, []]),
  );
};

const emptyQuestionOtherText = (): Record<string, string> => {
  return Object.fromEntries(
    agentBetaMultiSelectQuestions.map((question) => [question.slug, ""]),
  );
};

const toAgentBetaQuestionsPayload = (
  questionAnswers: Record<string, string[]>,
  questionOtherText: Record<string, string>,
): Record<string, string> => {
  return Object.fromEntries(
    Object.entries(questionAnswers)
      .map(([slug, values]) => {
        const csv = values
          .map((value) => {
            return value === AGENT_BETA_OTHER_OPTION_VALUE
              ? encodeAgentBetaOtherAnswer(questionOtherText[slug] ?? "")
              : value;
          })
          .join(",");
        return [slug, csv] as const;
      })
      .filter(([, csv]) => csv.length > 0),
  );
};

// NOTE (Yuxin, 2026-07-30): Use the schema's input type so form state can hold
// the unselected (`null`) revenue bucket; the refine narrows the parsed output
// back to `number` on submit.
export type AgentBetaFormValues = z.input<typeof agentBetaFormSchema>;
export type AgentBetaFormStep = "profile" | "account" | "workspace";
type AgentBetaOptInSource = "inviteModal" | "waitlistPage";

type WorkspaceOption = {
  id: string;
  name: string;
};

type SubmitState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "success" }
  | { status: "error"; message: string };

const getRevenueBucketIndex = (
  value: number | null | undefined,
): number | null => {
  if (value === null || value === undefined) {
    return null;
  }

  return agentBetaAnnualRevenueBuckets.reduce(
    (selectedIndex, bucket, index) => {
      return value >= bucket.value ? index : selectedIndex;
    },
    0,
  );
};

const isProfileType = (
  value: string | null | undefined,
): value is z.infer<typeof agentBetaProfileTypeSchema> => {
  return agentBetaProfileTypeSchema.safeParse(value).success;
};

const errorMessage = (error: unknown): string => {
  return error instanceof Error
    ? error.message
    : "Couldn't join Replo Agent Beta. Try again.";
};

export const useAgentBetaWaitlistForm = ({
  workspaceId,
  source,
}: {
  workspaceId?: string;
  source: AgentBetaOptInSource;
}) => {
  const { user, isAuthenticated, isLoading: isLoadingUser } = useCurrentUser();
  const logEvent = useLogAnalytics();
  // NOTE (Yuxin, 2026-07-28): Track whether the user signed up / logged in as
  // part of this opt-in (logged out on arrival, e.g. from the marketing email)
  // vs. was already authenticated (the in-app invite modal), so opt-in
  // attribution stays exact even when the account step ran anonymously.
  const authenticatedDuringOptInRef = React.useRef(false);
  // NOTE (Yuxin, 2026-08-02): Only the public waitlist page arrives from a
  // campaign link. The invite modal opens on top of an app route whose query
  // string is UI state, which would land in the admin table looking like
  // attribution. Snapshot once on mount because the signup and workspace steps
  // can rewrite the location.
  const urlParamsRef = React.useRef<Record<string, string> | null>(null);
  if (urlParamsRef.current === null) {
    urlParamsRef.current =
      source === "waitlistPage"
        ? collectAgentBetaUrlParams(window.location.search)
        : {};
  }
  const [step, setStep] = React.useState<AgentBetaFormStep>("profile");
  const [accountMode, setAccountMode] =
    React.useState<AgentBetaAccountMode>("signup");
  const [submitState, setSubmitState] = React.useState<SubmitState>({
    status: "idle",
  });
  const [workspaceOptions, setWorkspaceOptions] = React.useState<
    WorkspaceOption[]
  >([]);
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = React.useState<
    string[]
  >([]);
  // NOTE (Yuxin, 2026-07-18): `values` hydrates saved fields once auth resolves; keepDirtyValues protects in-progress edits.
  // NOTE (Yuxin, 2026-07-25): Validate only on submit so blurring a field (e.g. opening a dropdown) doesn't surface errors mid-flow.
  const form = useForm<AgentBetaFormValues>({
    resolver: zodResolver(agentBetaFormSchema),
    mode: "onSubmit",
    resetOptions: { keepDirtyValues: true },
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      profileType: "business",
      revenueBucketIndex: null,
      questionAnswers: emptyQuestionAnswers(),
      questionOtherText: emptyQuestionOtherText(),
      password: "",
    },
    values: user
      ? {
          firstName: user.firstName ?? "",
          lastName: user.lastName ?? "",
          email: user.email,
          profileType: isProfileType(user.profileType)
            ? user.profileType
            : "business",
          revenueBucketIndex: getRevenueBucketIndex(
            user.profileType === "agency"
              ? user.clientsAnnualRevenue
              : user.annualRevenue,
          ),
          questionAnswers: emptyQuestionAnswers(),
          questionOtherText: emptyQuestionOtherText(),
          password: "",
        }
      : undefined,
  });

  const createAccount = authRouter.createAccount.useMutation();
  const login = authRouter.login.useMutation();
  const createAccountWithGoogle =
    authRouter.createAccountWithGoogle.useMutation();
  const submitApplication = agentBetaRouter.submitApplication.useMutation();

  const submitToWorkspaces = React.useCallback(
    async ({
      workspaceIds,
      values,
    }: {
      workspaceIds: string[];
      values: AgentBetaFormValues;
    }) => {
      const annualRevenue =
        values.revenueBucketIndex === null
          ? undefined
          : agentBetaAnnualRevenueBuckets[values.revenueBucketIndex]?.value;
      if (annualRevenue === undefined) {
        throw new AgentBetaWaitlistError({
          message: "Select an annual revenue range",
        });
      }

      const agentBetaQuestions = {
        ...toAgentBetaQuestionsPayload(
          values.questionAnswers,
          values.questionOtherText,
        ),
        ...urlParamsRef.current,
      };
      await submitApplication.mutateAsync({
        workspaceIds,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        profileType: values.profileType,
        annualRevenue,
        ...(Object.keys(agentBetaQuestions).length > 0
          ? { agentBetaQuestions }
          : {}),
      });
      await userUtils.get.invalidate();
      logEvent("agent.beta.optIn.submitted", {
        source,
        workspaceCount: workspaceIds.length,
        authenticatedDuringOptIn: authenticatedDuringOptInRef.current,
        utmSource:
          new URLSearchParams(window.location.search).get("utm_source") ??
          undefined,
      });
      setSubmitState({ status: "success" });
    },
    [submitApplication, logEvent, source],
  );

  const resolveWorkspace = React.useCallback(
    async (values: AgentBetaFormValues) => {
      if (workspaceId) {
        await submitToWorkspaces({
          workspaceIds: [workspaceId],
          values,
        });
        return;
      }

      const { workspaces } =
        await workspaceUtils.getUserWorkspacesList.fetch(undefined);
      // Shopify-billed workspaces can't join the beta; with nothing eligible there's nothing to opt in, so land on the dashboard instead of an error.
      const eligibleWorkspaces = workspaces.filter(
        (workspace) => workspace.paymentProcessor !== "shopify",
      );
      if (eligibleWorkspaces.length === 0) {
        window.location.assign(routes.shops.default);
        return;
      }
      if (eligibleWorkspaces.length === 1 && eligibleWorkspaces[0]) {
        await submitToWorkspaces({
          workspaceIds: [eligibleWorkspaces[0].id],
          values,
        });
        return;
      }

      const options = eligibleWorkspaces.map(({ id, name }) => ({ id, name }));
      setWorkspaceOptions(options);
      setSelectedWorkspaceIds(
        [
          options.find(({ id }) => id === user?.workspace?.id)?.id ??
            options[0]?.id ??
            "",
        ].filter(Boolean),
      );
      setSubmitState({ status: "idle" });
      setStep("workspace");
    },
    [submitToWorkspaces, user?.workspace?.id, workspaceId],
  );

  const continueFromProfile = React.useCallback(
    async (values: AgentBetaFormValues) => {
      setSubmitState({ status: "idle" });
      if (!isAuthenticated) {
        setStep("account");
        return;
      }

      setSubmitState({ status: "submitting" });
      try {
        await resolveWorkspace(values);
      } catch (error) {
        setSubmitState({ status: "error", message: errorMessage(error) });
      }
    },
    [isAuthenticated, resolveWorkspace],
  );

  const submitAccount = React.useCallback(
    async (values: AgentBetaFormValues) => {
      const passwordIsValid =
        values.password.length >= 8 && values.password.length <= 100;
      if (!passwordIsValid) {
        form.setError("password", {
          message: "Use between 8 and 100 characters",
        });
        return;
      }

      setSubmitState({ status: "submitting" });
      try {
        authenticatedDuringOptInRef.current = true;
        const result =
          accountMode === "signup"
            ? await createAccount.mutateAsync({
                email: values.email.trim(),
                password: values.password,
                profileType: values.profileType,
                defaultProjectType: "shopify_theme_builder",
              })
            : await login.mutateAsync({
                email: values.email.trim(),
                rawPassword: values.password,
              });
        storeToken(result.token);
        await resolveWorkspace(values);
      } catch (error) {
        setSubmitState({ status: "error", message: errorMessage(error) });
      }
    },
    [accountMode, createAccount, form, login, resolveWorkspace],
  );

  const submitWithGoogle = React.useCallback(
    async (credentialResponse: CredentialResponse) => {
      const { clientId, credential } = credentialResponse;
      if (!clientId || !credential) {
        return;
      }
      setSubmitState({ status: "submitting" });
      try {
        authenticatedDuringOptInRef.current = true;
        const { token } = await createAccountWithGoogle.mutateAsync({
          clientId,
          credential,
          defaultProjectType: "shopify_theme_builder",
        });
        storeToken(token);
        await resolveWorkspace(form.getValues());
      } catch (error) {
        setSubmitState({ status: "error", message: errorMessage(error) });
      }
    },
    [createAccountWithGoogle, form, resolveWorkspace],
  );

  const setWorkspaceSelected = React.useCallback(
    ({ workspaceId, selected }: { workspaceId: string; selected: boolean }) => {
      setSelectedWorkspaceIds((currentWorkspaceIds) => {
        if (selected) {
          return currentWorkspaceIds.includes(workspaceId)
            ? currentWorkspaceIds
            : [...currentWorkspaceIds, workspaceId];
        }
        return currentWorkspaceIds.filter(
          (currentWorkspaceId) => currentWorkspaceId !== workspaceId,
        );
      });
    },
    [],
  );

  const submitSelectedWorkspaces = React.useCallback(async () => {
    if (selectedWorkspaceIds.length === 0) {
      return;
    }
    setSubmitState({ status: "submitting" });
    try {
      await submitToWorkspaces({
        workspaceIds: selectedWorkspaceIds,
        values: form.getValues(),
      });
    } catch (error) {
      setSubmitState({ status: "error", message: errorMessage(error) });
    }
  }, [form, selectedWorkspaceIds, submitToWorkspaces]);

  const selectAllWorkspaces = () => {
    setSelectedWorkspaceIds(workspaceOptions.map((workspace) => workspace.id));
  };

  return {
    form,
    step,
    accountMode,
    isAuthenticated,
    isLoadingUser,
    submitState,
    workspaceOptions,
    selectedWorkspaceIds,
    setAccountMode,
    setWorkspaceSelected,
    selectAllWorkspaces,
    continueFromProfile,
    submitAccount,
    submitWithGoogle,
    submitSelectedWorkspaces,
    goBack: () => {
      setSubmitState({ status: "idle" });
      if (step === "account") {
        setStep("profile");
      } else if (step === "workspace" && isAuthenticated) {
        setStep("profile");
      } else {
        setStep("account");
      }
    },
  };
};
